import React from 'react';
import { ERROR_SEVERITY, getStepSpecificErrorInfo } from '../utils/onboarding-errors';
import { getApiUrl } from '../utils/apiClient.js';
import LoadingSpinner from './LoadingSpinner';

const OnboardingErrorDisplay = ({ 
  error, 
  step, 
  retryCount = 0, 
  isRetrying = false, 
  onRetry, 
  onRecoveryAction,
  className=""
}) => {
  const errorInfo = getStepSpecificErrorInfo(error, step);
  
  const getSeverityStyles = (severity) => {
    switch (severity) {
      case ERROR_SEVERITY.LOW:
        return {
          container: 'bg-yellow-50 border-yellow-200',
          icon: 'text-yellow-600',
          title: 'text-yellow-800',
          description: 'text-yellow-700'
        };
      case ERROR_SEVERITY.MEDIUM:
        return {
          container: 'bg-orange-50 border-orange-200',
          icon: 'text-orange-600',
          title: 'text-orange-800',
          description: 'text-orange-700'
        };
      case ERROR_SEVERITY.HIGH:
      case ERROR_SEVERITY.CRITICAL:
        return {
          container: 'bg-red-50 border-red-200',
          icon: 'text-red-600',
          title: 'text-red-800',
          description: 'text-red-700'
        };
      default:
        return {
          container: 'bg-gray-50 border-gray-200',
          icon: 'text-gray-600',
          title: 'text-gray-800',
          description: 'text-gray-700'
        };
    }
  };

  const styles = getSeverityStyles(error.severity);

  const getErrorIcon = () => {
    switch (error.severity) {
      case ERROR_SEVERITY.LOW:
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case ERROR_SEVERITY.MEDIUM:
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case ERROR_SEVERITY.HIGH:
      case ERROR_SEVERITY.CRITICAL:
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div className={`rounded-lg border p-6 ${styles.container} ${className}`}>
      <div className="flex items-start">
        <div className={`flex-shrink-0 ${styles.icon}`}>
          {getErrorIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-lg font-semibold ${styles.title}`}>
            {errorInfo.title}
          </h3>
          <div className={`mt-2 text-sm ${styles.description}`}>
            <p>{errorInfo.description}</p>
            
            {/* Additional error details for development */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-3">
                <summary className="cursor-pointer font-medium">Technical Details</summary>
                <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono dark:bg-gray-700">
                  <p><strong>Type:</strong> {error.type}</p>
                  <p><strong>Severity:</strong> {error.severity}</p>
                  <p><strong>Retryable:</strong> {error.retryable ? 'Yes' : 'No'}</p>
                  <p><strong>Retry Count:</strong> {retryCount}</p>
                  {error.context && (
                    <p><strong>Context:</strong> {JSON.stringify(error.context, null, 2)}</p>
                  )}
                </div>
              </details>
            )}
          </div>

          {/* Recovery Actions */}
          {errorInfo.actions && errorInfo.actions.length > 0 && (
            <div className="mt-4">
              <div className="flex flex-wrap gap-2">
                {errorInfo.actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (action.action === 'retry' && onRetry) {
                        onRetry();
                      } else if (onRecoveryAction) {
                        onRecoveryAction(action.action);
                      }
                    }}
                    disabled={isRetrying && action.action === 'retry'}
                    className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md transition-colors ${
                      action.primary
                        ? 'border-transparent bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                    }`}
                  >
                    {isRetrying && action.action === 'retry' && (
                      <LoadingSpinner size="small" className="mr-2" />
                    )}
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Retry Information */}
          {error.retryable && retryCount > 0 && (
            <div className="mt-3 text-xs text-gray-500">
              Retry attempt {retryCount} of {error.maxRetries || 3}
            </div>
          )}

          {/* Network Status */}
          {error.type === 'network' && (
            <NetworkStatusIndicator className="mt-3" />
          )}
        </div>
      </div>
    </div>
  );
};

// Network status component
const NetworkStatusIndicator = ({ className="" }) => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const [isChecking, setIsChecking] = React.useState(false);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkConnection = async () => {
    setIsChecking(true);
    try {
      // Use the API config to get the proper URL
      const response = await fetch(getApiUrl('/api/health'), { 
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });
      setIsOnline(response.ok);
    } catch {
      setIsOnline(false);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className={`flex items-center text-xs ${className}`}>
      <div className={`w-2 h-2 rounded-full mr-2 ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
      <span className={isOnline ? 'text-green-600' : 'text-red-600'}>
        {isOnline ? 'Connected' : 'Connection issues detected'}
      </span>
      {!isOnline && (
        <button
          onClick={checkConnection}
          disabled={isChecking}
          className="ml-2 text-blue-600 hover:text-blue-800 disabled:opacity-50"
        >
          {isChecking ? 'Checking...' : 'Check again'}
        </button>
      )}
    </div>
  );
};

export default OnboardingErrorDisplay;
